import {http_client} from '@/plugins/http_client';

const urlBase = 'api';

// Helper para formatear fechas de yyyy-mm-dd a dd/mm/yyyy
const formatearFecha = (fecha) => {
  if (!fecha) return '';

  try {
    // Si ya está en formato dd/mm/yyyy, retornar tal cual
    if (fecha.includes('/')) {
      return fecha;
    }

    // Si está en formato yyyy-mm-dd o ISO (con T), convertir a dd/mm/yyyy
    if (fecha.includes('-')) {
      // Separar por 'T' o espacio para obtener solo la parte de la fecha
      const fechaSolo = fecha.split('T')[0].split(' ')[0];
      const [anio, mes, dia] = fechaSolo.split('-');
      if (!anio || !mes || !dia) return fecha;
      return `${dia}/${mes}/${anio}`;
    }

    return fecha;
  } catch (error) {
    console.error('Error al formatear fecha');
    return fecha;
  }
};

export const fetchSanciones = async params => {
  try {
    const response = await http_client(`${urlBase}/sanciones/listar-sanciones`, params, 'POST');
    const apiData = response?.data || response;

    // La data ahora viene anidada en apiData.data.sanciones
    const sancionesArray = apiData?.data?.sanciones || [];
    const mappedData = Array.isArray(sancionesArray)
      ? sancionesArray.map((item) => ({
        id: item.id,
        codigo: item.id,
        fecha_solicitud: formatearFecha(item.fechaSolicitud),
        tipo_sancion: item.tipoSancion?.nombre || item.tipoSancion || '',
        documento: item.empleado?.persona?.usuario?.documento || '',
        nombre: item.empleado?.persona?.usuario?.nombres || '',
        apellido: item.empleado?.persona?.usuario?.apellidos || '',
        nombreEmpleado: `${item.empleado?.persona?.usuario?.nombres || ''} ${item.empleado?.persona?.usuario?.apellidos || ''}`.trim(),
        unidad_organizativa: item.unidadOrganizativa?.nombre || '',
        // Nuevos campos visibles en tablas
        plaza: item.plaza || item.datosPlaza?.denominacionPlaza || item.plaza?.denominacion || '',
        cargo: item.cargo || item.puesto || item.puesto?.nombre || item.categoriaPuesto?.nombre || '',
        estado: item.estadoSolicitud?.nombre || item.estadoSolicitud || '',
        acuerdo: item.acuerdo || false,
        nivelGravedad: item.nivelGravedad || '',
      }))
      : [];

    // Los metadatos también están anidados en apiData.meta
    const meta = apiData?.data?.meta || {};

    return {
      data: mappedData,
      total: meta.total || 0,
      page: meta.current_page || 1,
      per_page: meta.per_page || 10,
      last_page: meta.last_page || 1,
    };

  } catch (error) {
    console.error('Ocurrió un error al obtener sanciones');
    return {data: [], total: 0, page: 1, per_page: 10, last_page: 1};
  }
};


// Obtener detalle de una solicitud (similar a fetchSancionEmpleado)
export const fetchSolicitudDetalle = async (id) => {
  try {
    const response = await http_client(`${urlBase}/sanciones/get-sancion-empleado/${id}`, {}, 'GET');
    const apiData = response?.data?.data || response;

    // Mapear la respuesta al formato esperado por la aplicación
    return {
      id,
      codigo: apiData.codigo || `SAN-${id}`,
      fecha_solicitud: apiData.fechaSolicitud || '',
      tipo_sancion: apiData.tipoSancion || '',
      nivel_gravedad: apiData.nivelGravedad || '',
      tipo_sancion_id: apiData.tipoSancionId || null, // Agregar el ID del tipo de sanción
      tipo_reporte: apiData.tipoSancion || '', // Mismo que tipo_sancion
      documento: apiData.datosPersonales?.numeroDocumento || '',
      nombre: apiData.datosPersonales?.empleadoNombre?.split(' ')[0] || '',
      apellido: apiData.datosPersonales?.empleadoNombre?.split(' ').slice(1).join(' ') || '',
      nombre_completo: apiData.datosPersonales?.empleadoNombre || '',
      unidad_organizativa: apiData.datosPersonales?.unidadOrganizativa || '',
      estado: apiData.estadoActual?.nombre || apiData.estadoSolicitud || 'Recibido',

      // Datos personales
      datosPersonales: {
        empleadoFoto: apiData.datosPersonales?.empleadoFoto || null,
        tipo_documento: apiData.datosPersonales?.tipoDocumento || '',
        numero_documento: apiData.datosPersonales?.numeroDocumento || '',
        pais_nacimiento: apiData.datosPersonales?.paisNacimiento || '',
        fecha_nacimiento: formatearFecha(apiData.datosPersonales?.fechaNacimiento),
        telefono_personal: apiData.datosPersonales?.telefonoPersonal || '',
        departamento_residencia: apiData.datosPersonales?.departamentoResidencia || '',
        distrito_residencia: apiData.datosPersonales?.distritoResidencia || '',
        direccion: apiData.datosPersonales?.direccion || '',
        email_institucional: apiData.datosPersonales?.emailInstitucional || '',
        fecha_contratacion: formatearFecha(apiData.datosPersonales?.fechaContratacion),
        establecimiento: apiData.datosPersonales?.establecimiento || '',
        unidad_organizativa: apiData.datosPersonales?.unidadOrganizativa || '',
        estado_empleado: apiData.datosPersonales?.estadoEmpleado || '',
        fecha_ingreso_gobierno: formatearFecha(apiData.datosPersonales?.fechaIngresoGobierno),
        modalidad_contratacion: apiData.datosPersonales?.modalidadContratacion || '',
        tiempo_servicio: apiData.datosPersonales?.tiempoServicio?.formato_largo || '', // Formato legible
      },

      // Datos de la plaza
      datosPlaza: {
        denominacion_plaza: apiData.datosPlaza?.denominacionPlaza || 'Titular',
        institucion: apiData.datosPlaza?.institucion || 'Ministerio de salud',
        numero_horas: apiData.datosPlaza?.numeroHoras || '8',
        cifrado_presupuestario: apiData.datosPlaza?.cifradoPresupuestario || '2025-3500-1-01-01-21-1',
        unidad_presupuestaria: apiData.datosPlaza?.unidadPresupuestaria || '01',
        linea_trabajo: apiData.datosPlaza?.lineaTrabajo || '02',
        partida: apiData.datosPlaza?.partida || '03',
        subpartida: apiData.datosPlaza?.subpartida || '1',
        tipo_financiamiento: apiData.datosPlaza?.tipoFinanciamiento || 'Fondos propios',
        salario_minimo_plaza: apiData.datosPlaza?.salarioMinimoPlaza || '$ 2,000.00',
        sueldo_maximo_plaza: apiData.datosPlaza?.sueldoMaximoPlaza || '$ 2,500.00',
        salario_empleado: apiData.datosPlaza?.salarioEmpleado || '$ 2,000.00',
        presupuesto_que_financia: apiData.datosPlaza?.presupuestoQueFinancia || '-',
        especifico_gasto: apiData.datosPlaza?.especificoGasto || '51101',
      },

      // Falta cometida
      falta_cometida: apiData.faltaCometida || '',

      // Archivos adjuntos
      archivos_adjuntos: Array.isArray(apiData.archivos?.adjuntos)
        ? apiData.archivos.adjuntos.map((archivo, index) => ({
          id: index + 1,
          nombre: archivo.nombre || '',
          tipo: archivo.tipo || '',
          ruta: archivo.url || '',
          url_descarga: archivo.url || '',
          base64_archivo: archivo.base64_archivo || archivo.base64 || null,
        }))
        : [],
      
      justificacion_juridica: Array.isArray(apiData.archivos?.acta_justificacion)
        ? apiData.archivos.acta_justificacion.map((archivo, index) => ({
          id: index + 1,
          nombre: archivo.nombre || '',
          tipo: archivo.tipo || '',
          ruta: archivo.url || '',
          url_descarga: archivo.url || '',
          base64_archivo: archivo.base64_archivo || archivo.base64 || null,
        }))
        : [],

      // Resolución externa
      resolucion_externa: Array.isArray(apiData.archivos?.resolucionExterna)
        ? apiData.archivos.resolucionExterna.map((archivo, index) => ({
          id: index + 1,
          nombre: archivo.nombre || '',
          tipo: archivo.tipo || '',
          ruta: archivo.url || '',
          url_descarga: archivo.url || '',
          // Incluir base64 si el backend lo provee
          base64_archivo: archivo.base64_archivo || archivo.base64 || null,
        }))
        : [],

      // Sanciones aplicables
      listaSanciones: apiData?.sancionesAplicadas || [],

      // Observaciones
      observaciones: apiData.observaciones || '',

      // URL del PDF del acuerdo
      // - Si estado es "Acuerdo generado": usar el primer acuerdo del array archivos.acuerdos
      // - Si estado es "Acuerdo firmado" o "Cerrado": obtener el UUID de archivos.acuerdo_firmado y hacer petición
      url_pdf_acuerdo: null, // Se asignará dinámicamente en el componente

      // Guardar datos necesarios para obtener el PDF
      acuerdo_data: (() => {
        const estadoActual = apiData.estadoActual?.nombre || apiData.estadoSolicitud || '';

        // Si está en estado "Acuerdo generado", usar acuerdos
        if (estadoActual === 'Acuerdo generado') {
          if (Array.isArray(apiData.archivos?.acuerdos) && apiData.archivos.acuerdos.length > 0) {
            return {
              tipo: 'url_directa',
              url: apiData.archivos.acuerdos[0].url || null,
            };
          }
        }

        // Si está "Acuerdo firmado" o "Cerrado", usar acuerdo_firmado con UUID
        if (estadoActual === 'Acuerdo firmado' || estadoActual === 'Cerrado') {
          if (Array.isArray(apiData.archivos?.acuerdo_firmado) && apiData.archivos.acuerdo_firmado.length > 0) {
            return {
              tipo: 'uuid',
              uuid: apiData.archivos.acuerdo_firmado[0].uuid || null,
            };
          }
        }

        return null;
      })(),

      // Historial de sanciones
      historial_sanciones: Array.isArray(apiData.historialSanciones)
        ? apiData.historialSanciones.map((sancion, index) => ({
          id: sancion.id || index + 1,
          numero: index + 1,
          fecha: formatearFecha(sancion.fecha),
          tipo_sancion: sancion.tipoSancion || '',
          nivel_gravedad: sancion.nivelGravedad || '',
          tipoSancion: sancion.tipoSancion || '', // Mantener ambos formatos
          tipoSancionId: sancion.tipoSancionId || null, // Agregar el ID del tipo de sanción
          // Mantener todos los detalles tal como vienen del backend
          detalles: sancion.detalles ? {
            tipoSancion: sancion.detalles.tipoSancion || '',
            tipoSancionId: sancion.detalles.tipoSancionId || sancion.tipoSancionId || null, // Agregar también en detalles
            faltaCometida: sancion.detalles.faltaCometida || '',
            archivos: sancion.detalles.archivos || {adjuntos: [], resolucionExterna: [], acuerdos: []},
            sancionesAplicadas: sancion.detalles.sancionesAplicadas || {},
            observaciones: sancion.detalles.observaciones || null,
          } : null,
        }))
        : [],
    };

  } catch (error) {
    console.error('Error al obtener detalle de solicitud RRHH');
    throw error;
  }
};

// Generar acuerdo
export const generarAcuerdo = async (solicitudId) => {
  try {
    const response = await http_client(`${urlBase}/sanciones/${solicitudId}/generar-acuerdo`, {}, 'GET');
    return response?.data || response;
  } catch (error) {
    console.error('Error al generar acuerdo');
    throw error;
  }
};

// Confirmar generación de acuerdo
export const confirmarGeneracionAcuerdo = (solicitudId) => {
  // return http_client(`${url}/${solicitudId}/confirmar-acuerdo`, {}, 'POST');

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        message: 'Acuerdo confirmado y enviado exitosamente',
      });
    }, 1000);
  });
};

// Subir archivo firmado
export const subirArchivoFirmado = (acuerdoId, archivo) => {
  // const formData = new FormData();
  // formData.append('archivo', archivo);
  // return http_client(`${url}/acuerdos/${acuerdoId}/subir-firmado`, formData, 'POST');

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        message: 'Archivo firmado subido exitosamente',
        archivo: archivo.name,
      });
    }, 1500);
  });
};

// Enviar acuerdo a firmar
export const enviarAcuerdo = async (solicitudId, archivo) => {
  try {
    const formData = new FormData();
    formData.append('documento', archivo);

    const response = await http_client(
      `${urlBase}/sanciones/${solicitudId}/enviar-acuerdo`,
      formData,
      'POST',
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response?.data || response;
  } catch (error) {
    console.error('Error al enviar acuerdo a firmar');
    throw error;
  }
};

// Cerrar reporte (enviar a planilla)
export const cerrarReporte = async (solicitudId) => {
  try {
    const response = await http_client(
      `${urlBase}/sanciones/${solicitudId}/cerrar-sancion`,
      {},
      'GET'
    );

    return response?.data || response;
  } catch (error) {
    console.error('Error al cerrar reporte');
    throw error;
  }
};

// Obtener PDF de archivo adjunto
export const obtenerPDFArchivo = (archivoId) => {
  // return http_client(`${url}/archivos/${archivoId}/pdf`, {}, 'GET');

  // PDF fake en base64 (1 página simple)
  const pdfBase64 = 'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL01lZGlhQm94WzAgMCA2MTIgNzkyXS9Db250ZW50cyA0IDAgUi9SZXNvdXJjZXM8PC9Gb250PDwvRjEgNSAwIFI+Pj4+Pj4KZW5kb2JqCjQgMCBvYmoKPDwvTGVuZ3RoIDQ0Pj4Kc3RyZWFtCkJUCi9GMSAxMiBUZgoxMDAgNzAwIFRkCihBcmNoaXZvIEFkanVudG8pIFRqCkVUCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PC9UeXBlL0ZvbnQvU3VidHlwZS9UeXBlMS9CYXNlRm9udC9IZWx2ZXRpY2E+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKNiAwIG9iago8PC9DcmVhdG9yKE1pY3Jvc29mdCBXb3JkKS9DcmVhdGlvbkRhdGUoRDoyMDI0MDEwMTEyMDAwMCk+PgplbmRvYmoKeHJlZgowIDcKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMzY4IDAwMDAwIG4gCjAwMDAwMDAzMTcgMDAwMDAgbiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMTI1IDAwMDAwIG4gCjAwMDAwMDAyMTggMDAwMDAgbiAKMDAwMDAwMDQxNyAwMDAwMCBuIAp0cmFpbGVyCjw8L1NpemUgNy9Sb290IDEgMCBSL0luZm8gNiAwIFI+PgpzdGFydHhyZWYKNDk3CiUlRU9G';

  return Promise.resolve(pdfBase64);
};

// Obtener PDF de acuerdo
export const obtenerPDFAcuerdo = (acuerdoId) => {
  // return http_client(`${url}/acuerdos/${acuerdoId}/pdf`, {}, 'GET');

  // PDF fake en base64
  const pdfBase64 = 'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL01lZGlhQm94WzAgMCA2MTIgNzkyXS9Db250ZW50cyA0IDAgUi9SZXNvdXJjZXM8PC9Gb250PDwvRjEgNSAwIFI+Pj4+Pj4KZW5kb2JqCjQgMCBvYmoKPDwvTGVuZ3RoIDQ0Pj4Kc3RyZWFtCkJUCi9GMSAxMiBUZgoxMDAgNzAwIFRkCihBY3VlcmRvIGRlIFNhbmNpw7NuKSBUagpFVAplbmRzdHJlYW0KZW5kb2JqCjUgMCBvYmoKPDwvVHlwZS9Gb250L1N1YnR5cGUvVHlwZTEvQmFzZUZvbnQvSGVsdmV0aWNhPj4KZW5kb2JqCjIgMCBvYmoKPDwvVHlwZS9QYWdlcy9LaWRzWzMgMCBSXS9Db3VudCAxPj4KZW5kb2JqCjEgMCBvYmoKPDwvVHlwZS9DYXRhbG9nL1BhZ2VzIDIgMCBSPj4KZW5kb2JqCjYgMCBvYmoKPDwvQ3JlYXRvcihNaWNyb3NvZnQgV29yZCkvQ3JlYXRpb25EYXRlKEQ6MjAyNDAxMDExMjAwMDApPj4KZW5kb2JqCnhyZWYKMCA3CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDM2OCAwMDAwMCBuIAowMDAwMDAwMzE3IDAwMDAwIG4gCjAwMDAwMDAwMDkgMDAwMDAgbiAKMDAwMDAwMDEyNSAwMDAwMCBuIAowMDAwMDAwMjE4IDAwMDAwIG4gCjAwMDAwMDA0MTcgMDAwMDAgbiAKdHJhaWxlcgo8PC9TaXplIDcvUm9vdCAxIDAgUi9JbmZvIDYgMCBSPj4Kc3RhcnR4cmVmCjQ5NwolJUVPRg==';

  return Promise.resolve(pdfBase64);
};

// Descargar acuerdo generado
export const descargarAcuerdoGenerado = (solicitudId) => {
  // return http_client(`${url}/${solicitudId}/acuerdo-generado/download`, {}, 'GET');

  return new Promise((resolve) => {
    setTimeout(() => {
      // Simular descarga de archivo
      const pdfBase64 = 'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL01lZGlhQm94WzAgMCA2MTIgNzkyXS9Db250ZW50cyA0IDAgUi9SZXNvdXJjZXM8PC9Gb250PDwvRjEgNSAwIFI+Pj4+Pj4KZW5kb2JqCjQgMCBvYmoKPDwvTGVuZ3RoIDU1Pj4Kc3RyZWFtCkJUCi9GMSAxMiBUZgoxMDAgNzAwIFRkCihBY3VlcmRvX3NhbmNpw7NuMTMxMy5wZGYpIFRqCkVUCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PC9UeXBlL0ZvbnQvU3VidHlwZS9UeXBlMS9CYXNlRm9udC9IZWx2ZXRpY2E+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKNiAwIG9iago8PC9DcmVhdG9yKE1pY3Jvc29mdCBXb3JkKS9DcmVhdGlvbkRhdGUoRDoyMDI0MDEwMTEyMDAwMCk+PgplbmRvYmoKeHJlZgowIDcKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMzY4IDAwMDAwIG4gCjAwMDAwMDAzMTcgMDAwMDAgbiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMTI1IDAwMDAwIG4gCjAwMDAwMDAyMjkgMDAwMDAgbiAKMDAwMDAwMDQyOCAwMDAwMCBuIAp0cmFpbGVyCjw8L1NpemUgNy9Sb290IDEgMCBSL0luZm8gNiAwIFI+PgpzdGFydHhyZWYKNTA4CiUlRU9G';
      resolve({
        success: true,
        filename: `Acuerdo_sancion${solicitudId}.pdf`,
        data: pdfBase64,
      });
    }, 500);
  });
};

// Obtener estados del catálogo
export const fetchEstados = async () => {
  try {
    const response = await http_client('api/catalogos/sanciones-estados', {}, 'GET');
    const apiData = response?.data || response;

    // Normalizar la respuesta
    const estados = apiData?.data || apiData || [];

    // Mapear a formato {id, nombre}
    const normalized = Array.isArray(estados)
      ? estados.map(e => ({
        id: e.id || e.value || null,
        nombre: e.nombre || e.label || e.descripcion || e.name || '',
      }))
      : [];

    return normalized;
  } catch (error) {
    return [];
  }
};

// Obtener documento firmado por UUID (devuelve base64)
export const obtenerDocumentoFirmado = async (uuid) => {
  try {
    const baseUrl = process.env.VUE_APP_API_EXTERNA_FIRMA_DOCUMENTOS_URL;
    const url = `${baseUrl}/api/documentos/${uuid}`;

    const response = await http_client(url, {}, 'GET');
    const data = response?.data || response;

    // El endpoint devuelve { uuid: "...", archivo: "data:application/pdf;base64,..." }
    // Retornar directamente el base64
    return data?.archivo || data?.data?.archivo || null;
  } catch (error) {
    console.error('Error al obtener documento firmado por UUID');
    throw error;
  }
};

export default {
  fetchSanciones,
  fetchSolicitudDetalle,
  generarAcuerdo,
  confirmarGeneracionAcuerdo,
  subirArchivoFirmado,
  enviarAcuerdo,
  cerrarReporte,
  obtenerPDFArchivo,
  obtenerPDFAcuerdo,
  descargarAcuerdoGenerado,
  fetchEstados,
  obtenerDocumentoFirmado,
};
